/**
 * VerifiedOriginalSeal — das WL-K11-Siegel (architecture-loop-b.md §4.8,
 * legal-texts-loop-b.md §6b), differenziert nach `verificationSource`.
 *
 * Zeigt an einem Eingangsbeleg an, wie stark dessen strukturierte Daten mit dem
 * über FaeBill versendeten Original des Ausstellers übereinstimmen — KEIN
 * OCR-Raten (§1.0-Moat). Der begleitende Scope-Hinweis grenzt das Siegel bewusst
 * gegen eine „Echtheits-/Richtigkeits"-Anmutung ab (§5 UWG).
 *
 * W1 (§4.8 v3.1, Reviewer-REJECT Bündel 1d): Die Behauptungsstärke MUSS zur
 * Match-Quelle passen, sonst irreführend (§5 UWG):
 *  - `claim`/`hash` = byte-Identität nachgewiesen → Marker `siegel-verified-invoice`
 *    („Verifiziert gegen Original") + `siegel-verified-tooltip`.
 *  - `invoice_ref` = nur weicher ID-Abgleich (Rechnungsnr.+USt-IdNr.), KEINE
 *    byte-Identität → schwächerer Marker `siegel-ref-label` („Beleg zugeordnet")
 *    + `siegel-ref-tooltip`.
 *
 * SICHTBARKEIT (fail-closed, doppelt gegated):
 *  - `enabled` (Cluster-D-Gate des Betrachters, `canUseDocChecks(uid, sellerData)`
 *    — M-GA-4; `GA_FLAG || isLoopBBeta`),
 *  - UND `verified.verifiedAgainstOriginal === true` (echter Match).
 * Fehlt eine Bedingung ⇒ rendert `null` (kein Siegel bei „vermutlich echt").
 *
 * WORDING (Jurist-Wortlaut 1:1, legal-texts-loop-b.md §6b — J-1): Labels und
 * Scope-Texte sind wörtlich übernommen. Wording-VETO (§2.10): „fälschungssicher/
 * revisionssicher/geprüft/garantiert echt/rechtssicher/GoBD-konform" kommen in
 * KEINER Variante vor.
 *
 * @module VerifiedOriginalSeal
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconGobd, IconLink } from '../../icons';
import type { InboundVerified } from '../../types/inboundTypes';

interface VerifiedOriginalSealProps {
  /** Der `verified`-Map am Beleg (backend-only geschrieben), oder null/undefined. */
  verified: InboundVerified | null | undefined;
  /**
   * Cluster-D-Gate des Betrachters: `canUseDocChecks(currentUser.uid, sellerData)`
   * (M-GA-4; `GA_FLAG || isLoopBBeta`). Vom Aufrufer berechnet und durchgereicht
   * (kein `useAuth` in dieser rein präsentationalen Komponente). Fail-closed:
   * `false` ⇒ kein Siegel.
   */
  enabled: boolean;
}

// Exakter Jurist-Wortlaut (legal-texts-loop-b.md §6b) als i18n-Fallback.

/** `siegel-verified-invoice` — claim/hash (byte-Identität nachgewiesen). */
const VERIFIED_LABEL_DE = 'Verifiziert gegen Original';
/** `siegel-verified-tooltip` — claim/hash. */
const VERIFIED_TOOLTIP_DE =
  'Dieses Siegel bestätigt, dass die angezeigten strukturierten Daten technisch – ' +
  'byte-identisch – mit der in dieser Datei eingebetteten Original-Rechnungsdatei ' +
  '(z. B. ZUGFeRD-XML) übereinstimmen; nachgewiesen über einen kryptografischen ' +
  'Abgleich (Hash-Vergleich bzw. vom System signierte Bestätigung). Es ist keine ' +
  'Bestätigung der steuerlichen oder inhaltlichen Richtigkeit der Rechnung und keine ' +
  'Aussage darüber, ob die Rechnung berechtigt ist.';

/** `siegel-ref-label` — invoice_ref (nur ID-Abgleich, keine byte-Identität). */
const REF_LABEL_DE = 'Beleg zugeordnet';
/** `siegel-ref-tooltip` — invoice_ref. */
const REF_TOOLTIP_DE =
  'Dieser Eingangsbeleg wurde einem bei FaeBill bekannten Ausgangs-/Referenzbeleg ' +
  'zugeordnet: Rechnungsnummer und USt-IdNr. des Ausstellers stimmen mit diesem ' +
  'bekannten Beleg überein. Dabei handelt es sich um einen Abgleich anhand von ' +
  'Kennzeichen – es ist nicht nachgewiesen, dass die hier vorliegende Datei mit dem ' +
  'Original byte-identisch ist. Die Zuordnung ist keine Bestätigung der steuerlichen ' +
  'oder inhaltlichen Richtigkeit der Rechnung und keine Aussage darüber, ob die ' +
  'Rechnung berechtigt ist.';

const VerifiedOriginalSeal: React.FC<VerifiedOriginalSealProps> = ({ verified, enabled }) => {
  const { t } = useTranslation('posteingang');

  // Doppeltes fail-closed Gate: Beta-Betrachter UND echter Original-Match.
  if (!enabled || verified?.verifiedAgainstOriginal !== true) {
    return null;
  }

  // W1: Behauptungsstärke folgt der Match-Quelle. `invoice_ref` = schwächeres
  // Label/Tooltip; alles andere (claim/hash) = volles Verified-Siegel.
  const isRefOnly = verified.verificationSource === 'invoice_ref';

  const label = isRefOnly
    ? t('verifiedSeal.refLabel', REF_LABEL_DE)
    : t('verifiedSeal.label', VERIFIED_LABEL_DE);
  const scope = isRefOnly
    ? t('verifiedSeal.refTooltip', REF_TOOLTIP_DE)
    : t('verifiedSeal.tooltip', VERIFIED_TOOLTIP_DE);

  // Distinkte Optik: emerald „Schild" für die byte-Identität, neutrales
  // slate „Kette" für die weichere Zuordnung — die Optik überzeichnet nicht.
  const badgeClass = isRefOnly
    ? 'inline-flex items-center gap-2 rounded-full bg-slate-700/40 border border-slate-500/40 px-3 py-1.5 text-sm font-semibold text-slate-200'
    : 'inline-flex items-center gap-2 rounded-full bg-[var(--tone-success-bg)] border border-emerald-500/40 px-3 py-1.5 text-sm font-semibold text-[var(--tone-success-fg)]';

  return (
    <div className="mb-4">
      <span className={badgeClass} title={scope}>
        {isRefOnly ? (
          <IconLink className="text-slate-400" size={14} />
        ) : (
          <IconGobd className="text-[var(--tone-success-fg)]" size={14} />
        )}
        {label}
      </span>
      {/* Scope-Hinweis IMMER sichtbar (nicht nur Hover) — der rechtlich gebotene
          Abgrenzungstext darf auf Touch/Screenreader nicht verborgen sein. */}
      <p className="mt-1.5 max-w-prose text-xs leading-relaxed text-muted">
        {scope}
      </p>
    </div>
  );
};

export default VerifiedOriginalSeal;
